// =============================================
// TradingViewWidget.jsx - TradingView 공식 스크립트 방식
// =============================================
// new window.TradingView.widget() 방식으로 전환
// widgetbar 설정을 통해 우측 패널 완전 제거
// =============================================

import React, { useEffect, useRef, useState, memo } from 'react';
import ErrorBoundary from './ErrorBoundary';


// =============================================
// TradingView 위젯 내부 컴포넌트 (스크립트 방식)
// =============================================
function TradingViewWidgetInner({
  symbol   = 'BINANCE:BTCUSDT',
  interval = 'D',
  height   = 800,
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // TradingView 스크립트가 이미 로드되어 있는지 확인
    const initWidget = () => {
      if (!containerRef.current) return;
      if (!window.TradingView || !window.TradingView.widget) {
        console.error('TradingView 라이브러리가 로드되지 않았습니다.');
        setHasError(true);
        setIsLoading(false);
        return;
      }

      try {
        // 이전 위젯 제거
        if (widgetRef.current) {
          widgetRef.current.remove();
          widgetRef.current = null;
        }

        // 컨테이너 비우기
        containerRef.current.innerHTML = '';

        // ★★★ new TradingView.widget() 설정 객체 ★★★
        widgetRef.current = new window.TradingView.widget({
          // ── 기본 설정 ──
          symbol: symbol,              // 예: "BINANCE:BTCUSDT"
          interval: interval,          // TradingView 봉 단위: "D", "60", "15", "W" 등
          container_id: containerRef.current.id,

          // ── 외관 설정 ──
          theme: 'dark',
          style: '1',                  // 1 = 캔들스틱 차트
          locale: 'kr',
          timezone: 'Asia/Seoul',
          toolbar_bg: '#1a202c',

          // ── 차트 크기 ──
          width: '100%',
          height: height - 40,         // 디버그 바 공간 제외

          // ── 기능 활성화/비활성화 ──
          allow_symbol_change: true,
          hide_top_toolbar: false,     // 상단 툴바는 유지 (차트 컨트롤 필요)
          hide_side_toolbar: true,     // 우측 도구막대 숨김
          save_image: false,

          // ★★★ 우측 패널 제거 핵심 설정 ★★★
          hotlist: false,              // 인기 종목 목록 (KRX, 거래량 상승주) 완전히 제거
          calendar: false,             // 경제 캘린더 제거
          details: false,              // 상세 정보 제거
          watchlist_settings: {
            enabled: false,
          },

          // ★★★ widgetbar 객체 - 가장 확실한 방법 ★★★
          widgetbar: {
            details: false,            // 상세 정보 패널 숨김
            watchlist: false,          // 관심 종목 목록 숨김
            hotlist: false,            // 인기 종목 목록 (KRX, 거래량 상승주) 숨김
            calendar: false,           // 경제 캘린더 숨김
            datawindow: false,         // 데이터 윈도우 숨김
          },

          // ── 기타 설정 ──
          disabled_features: [
            'header_symbol_search',
            'header_compare',
            'header_undo_redo',
            'header_screenshot',
            'header_saveload',
          ],
          enabled_features: [
            'study_templates',
          ],

          // ── 로딩 완료 콜백 ──
          onload: () => {
            setIsLoading(false);
            setHasError(false);
          },
        });

      } catch (error) {
        console.error('TradingView 위젯 생성 실패:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    // TradingView 스크립트 로드
    if (!document.getElementById('tradingview-widget-script')) {
      const script = document.createElement('script');
      script.id = 'tradingview-widget-script';
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = initWidget;
      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      document.head.appendChild(script);
    } else {
      // 스크립트가 이미 로드된 경우
      if (window.TradingView) {
        initWidget();
      } else {
        // 스크립트는 있지만 아직 로드 중
        const checkInterval = setInterval(() => {
          if (window.TradingView) {
            clearInterval(checkInterval);
            initWidget();
          }
        }, 100);

        // 10초 후에도 로드 안 되면 에러
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.TradingView) {
            setHasError(true);
            setIsLoading(false);
          }
        }, 10000);
      }
    }

    // 클린업: 컴포넌트 언마운트 시 위젯 제거
    return () => {
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
        } catch (e) {
          console.warn('위젯 제거 중 오류:', e);
        }
        widgetRef.current = null;
      }
    };
  }, [symbol, interval, height]);

  // 재시도 핸들러
  const handleRetry = () => {
    setHasError(false);
    setIsLoading(true);
    window.location.reload();
  };

  return (
    <div style={{ height: `${height}px`, width: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── 디버그 표시줄 ── */}
      <div style={{
        flexShrink: 0,
        backgroundColor: '#0d1117',
        color: '#63b3ed',
        fontSize: '0.75rem',
        fontFamily: 'monospace',
        padding: '5px 14px',
        borderBottom: '1px solid #2d3748',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <span style={{ color: '#718096' }}>현재 보고 있는 종목:</span>
        <strong style={{ color: '#90cdf4', letterSpacing: '0.04em' }}>{symbol}</strong>
        <span style={{ color: '#4a5568', marginLeft: '0.5rem', fontSize: '0.7rem' }}>
          (TradingView 스크립트 방식 · widgetbar 설정 적용됨)
        </span>
      </div>

      {/* ── 차트 컨테이너 ── */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#1a202c' }}>

        {/* 로딩 오버레이 */}
        {isLoading && !hasError && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '0.75rem',
            backgroundColor: '#1a202c',
            zIndex: 10,
            color: '#4a5568',
            fontSize: '0.9rem',
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              border: '3px solid #2d3748',
              borderTop: '3px solid #3182ce',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <span>차트 로딩 중...</span>
            <span style={{ fontSize: '0.75rem' }}>{symbol} · TradingView</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* 에러 화면 */}
        {hasError && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '0.75rem',
            backgroundColor: '#1a202c',
            zIndex: 10,
            color: '#718096',
            fontSize: '0.9rem',
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <span style={{ color: '#e2e8f0' }}>차트를 불러오는 중 오류가 발생했습니다.</span>
            <span style={{ fontSize: '0.8rem' }}>TradingView 스크립트 로드 실패</span>
            <button
              onClick={handleRetry}
              style={{
                marginTop: '0.5rem',
                padding: '0.4rem 1.2rem',
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              페이지 새로고침
            </button>
          </div>
        )}

        {/* TradingView 위젯이 렌더링될 컨테이너 */}
        <div
          id={`tradingview-widget-${symbol}-${interval}`}
          ref={containerRef}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
}


// =============================================
// 외부 공개 컴포넌트 — ErrorBoundary + memo
// =============================================
function TradingViewWidget(props) {
  return (
    <ErrorBoundary height={props.height}>
      <TradingViewWidgetInner {...props} />
    </ErrorBoundary>
  );
}

export default memo(TradingViewWidget);
